import { useEffect, useMemo } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import { useDispatch, useSelector } from 'react-redux'

import { toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'
import { getAccountId, isAccountTypeBrand } from 'store/currentUser/selectors'
import { INIT_PROFILE } from 'store/storefront/constants'
import {
  getBrandId,
  getProfileFetched,
  getProfileInfo,
} from 'store/storefront/selectors'

import { ProductLine } from './BrandDetails'
import { formatProfile } from './BrandDetails.formatter'
import {
  BRAND_PRODUCT_LINES,
  UPSERT_BRAND_PROFILE,
} from './BrandDetails.queries'
import { getStorefrontProfileUpsertInput } from 'features/Storefront/StorefrontNavbar/StorefrontNavbar.transformations'
import { BRAND_PROFILE } from 'features/Storefront/StorefrontWidgets.queries.jsx'

const useProductLines = () => {
  const { loading, data, error } = useQuery(BRAND_PRODUCT_LINES, {
    client: atlasClient,
  })
  const productLines: ProductLine[] = useMemo(
    () => data?.brandProductLines || [],
    [data],
  )
  return {
    productLines,
    loading,
    error,
  }
}

const useBrandProfile = (brandId: string, profileFetched: boolean) => {
  const { loading, data, error } = useQuery(BRAND_PROFILE, {
    client: atlasClient,
    variables: { brandIds: [brandId] },
    skip: !brandId || profileFetched,
  })
  const brandProfile = useMemo(
    () => data?.brandProfiles?.edges?.[0]?.node || {},
    [data],
  )
  return {
    brandProfile,
    loading,
    error,
  }
}

/**
 * The selected categories live in two independent places: `brandProductLines`
 * flags them with `active`, and the brand profile lists them by name. We union
 * both so the selection survives regardless of which source is populated.
 */
export const getSelectedProductLineIds = (
  productLines: ProductLine[],
  savedProductLineNames: string[] = [],
): string[] => {
  const savedNames = new Set(savedProductLineNames)
  return productLines
    .filter(
      (productLine: ProductLine) =>
        productLine?.active || savedNames.has(productLine?.name),
    )
    .map((productLine: ProductLine) => productLine?.id)
}

export const useProfileInfo = () => {
  const dispatch = useDispatch()
  let brandId = useSelector(getBrandId)
  const profileFetched = useSelector((state) => getProfileFetched(state))
  const isBrandAccount = useSelector(isAccountTypeBrand)
  const accountId = useSelector(getAccountId)
  const { renderErrorToast } = useRenderToasters()

  if (!brandId) {
    brandId = isBrandAccount ? toGlobalId('Brand', accountId) : ''
  }
  const {
    error: productLinesErrors,
    loading: productLinesLoading,
    productLines,
  } = useProductLines()
  const {
    error: brandProfileErrors,
    loading: brandProfileLoading,
    brandProfile,
  } = useBrandProfile(brandId, profileFetched)

  useEffect(() => {
    // Both queries feed the profile, so initialising before either one settles
    // would snapshot an incomplete profile that never gets corrected.
    if (productLinesLoading || brandProfileLoading) {
      return
    }
    if (isEmpty(brandProfile)) {
      if (productLinesErrors || brandProfileErrors) {
        renderErrorToast({
          description: 'There was a problem loading your profile.',
        })
      }
      return
    }
    const profile = formatProfile({
      ...brandProfile,
      productLineIds: getSelectedProductLineIds(
        productLines,
        brandProfile?.productLines,
      ),
    })
    dispatch({ type: INIT_PROFILE, value: profile })
    // renderErrorToast is not referentially stable, and re-running INIT_PROFILE
    // resets backupProfile, which would discard the user's unsaved edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productLinesLoading, brandProfileLoading, brandProfile, productLines])

  return {
    loading: brandProfileLoading || productLinesLoading,
    productLines,
  }
}

export const useUpsertStorefrontProfile = () => {
  const profile = useSelector(getProfileInfo)

  const [upsertStorefront] = useMutation(UPSERT_BRAND_PROFILE, {
    client: atlasClient,
  })

  return () => {
    return upsertStorefront({
      variables: {
        input: getStorefrontProfileUpsertInput(profile),
      },
    })
  }
}
